<template>
  <div class="w-screen h-screen flex">
    <!-- Left Section -->
    <div
      class="left-section w-1/2 flex flex-col items-center justify-center p-10"
    >
      <figure class="logo-container mb-8">
        <img src="@/assets/main-logo.png" alt="Logo" class="h-20" />
      </figure>
    </div>

    <!-- Right Section -->
    <div class="w-1/2 bg-white flex items-center justify-center">
      <div
        class="bg-white shadow-lg rounded-xl p-8 max-w-md w-full border border-gray-200"
      >
        <!-- Title -->
        <div class="text-xl font-semibold text-gray-800 mb-6 text-center">
          Dynamic Builder
        </div>

        <!-- Email Input -->
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-medium mb-2"
            >Email</label
          >
          <input
            class="custom-input custom-focus"
            type="email"
            v-model="email"
            placeholder="Enter your email"
            @keyup.enter="login"
          />
        </div>

        <!-- Password Input -->
        <div class="mb-4 relative">
          <label class="block text-gray-700 text-sm font-medium mb-2"
            >Password</label
          >
          <input
            :type="showPassword ? 'text' : 'password'"
            class="custom-input custom-focus pr-10"
            v-model="password"
            placeholder="Enter your password"
            @keyup.enter="login"
          />
          <!-- Eye Icon -->
          <button
            type="button"
            class="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
            @click="togglePasswordVisibility"
          >
            <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
        </div>

        <!-- Error Message -->
        <div
          v-if="error"
          class="error-message flex items-center justify-between p-3 mb-4"
        >
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle text-red-600 mr-2 mt-0.5"></i>
            <span class="text-red-600 font-medium">
              {{ error }}
              <span
                v-if="resetRequired"
                class="text-blue-600 underline ml-1 cursor-pointer"
                @click="goToConfirmReset"
              >
                Click here to continue.
              </span>
            </span>
          </div>
          <button
            @click="
              () => {
                error = '';
                resetRequired = false;
              }
            "
            class="text-red-600 hover:text-red-800 ml-2"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Reset Password Link -->
        <div
          v-if="!resetRequired"
          class="text-sm register-text mb-4 cursor-pointer"
          @click.stop="$router.push({ path: '/forgot-password' })"
        >
          Forgot password?
        </div>

        <!-- Login Button -->
        <button
          @click="login"
          :disabled="!isLoginEnabled || isLoading || resetRequired"
          class="custom-button w-full flex items-center justify-center"
        >
          <span v-if="!isLoading">Login</span>
          <span v-else class="auth-loader"></span>
        </button>

        <!-- Register Link -->
        <div class="mt-4 text-sm text-center">
          New to Builder?
          <router-link to="register" class="register-text">
            Register
          </router-link>
        </div>
      </div>

      <!-- Reset Password Modal -->
      <vue-final-modal
        classes="flex justify-center items-center"
        v-model="resetPassword"
      >
        <div
          class="bg-white shadow-lg rounded-lg p-8 max-w-md w-full border border-gray-200"
        >
          <div class="text-xl font-semibold text-gray-800 mb-6 text-center">
            Request Reset Password
          </div>
          <input
            class="custom-input custom-focus"
            type="email"
            v-model="email"
            placeholder="Enter your email"
          />
          <button @click="requestReset" class="custom-button w-full mt-5">
            Send Reset Request
          </button>
        </div>
      </vue-final-modal>
    </div>
  </div>
</template>

<script>
import * as authService from "../service/auth";
import { setAuthState } from "../service/authState";

export default {
  name: "Login",
  data() {
    return {
      email: "",
      password: "",
      error: "",
      resetPassword: false,
      isLoading: false,
      showPassword: false,
      resetRequired: false,
    };
  },
  computed: {
    isLoginEnabled() {
      return this.email.trim() !== "" && this.password.trim() !== "";
    },
  },
  methods: {
    async login() {
      if (!this.email || !this.password) {
        return;
      }

      this.isLoading = true;
      this.error = "";
      this.resetRequired = false;

      try {
        await authService.loginApi(this.email, this.password);
        setAuthState(true);
        const redirect = this.$route.query.redirect || '/site-list';
        this.$router.push(redirect);
      } catch (err) {
        console.log("Login Error:", err);

        if (err?.error?.includes("User is not confirmed.")) {
          localStorage.setItem("pendingPassword", this.password);
          this.$router.push({
            path: "/email-verification",
            query: { email: this.email },
          });
        } else if (
          typeof err === "object" &&
          err?.requiresReset &&
          err?.nextStep === "CONFIRM_FORGOT_PASSWORD"
        ) {
          this.error = err.error;
          this.resetRequired = true;
        } else if (typeof err === "object" && err.error) {
          this.error = err.error;
        } else {
          this.error = "An error occurred. Please try again.";
        }
      } finally {
        this.isLoading = false;
      }
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    async requestReset() {
      await authService.requestResetPassword(this.email);
      this.$notify({
        type: "success",
        text: "Request sent",
      });
      this.resetPassword = false;
    },
    goToConfirmReset() {
      this.$router.push({
        path: "/confirm-forgot-password",
        query: { email: this.email },
      });
    },
  },
};
</script>
